import { EM_DASH, parseIso } from "@altek/ui/lib/format/date";
import { todayIso } from "./today";

/**
 * İNSAN DİLİYLE TARİH (ikinci tasarım turu, 3 Kasım 2026; yönerge §2): «8 Kas 2026», «4–5 Ara 2026», yakın işte «3 gün sonra», «2 gün gecikti».
 * Hesap takvim günüyle, İstanbul gününe göre (`todayIso`); `new Date(gün)` UTC gece yarısı okur, kullanılmaz.
 */
const AY = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

/** `2026-11-08` → «8 Kas 2026». Yoksa «—». */
export function humanDate(value: string | null | undefined): string {
  const date = parseIso(value?.slice(0, 10) ?? null);
  return date ? `${String(date.day)} ${AY[date.month - 1] ?? ""} ${String(date.year)}` : EM_DASH;
}

/** Bir an, okuyanın saatiyle: «25 Eki 2026 14:20». Yoksa «—». */
export function humanDateTime(value: string | null | undefined): string {
  if (!value) return EM_DASH;
  const moment = new Date(value);
  if (Number.isNaN(moment.getTime())) return EM_DASH;
  const clock = `${String(moment.getHours()).padStart(2, "0")}:${String(moment.getMinutes()).padStart(2, "0")}`;
  return `${String(moment.getDate())} ${AY[moment.getMonth()] ?? ""} ${String(moment.getFullYear())} ${clock}`;
}

/** «4–5 Ara 2026», «28 Kas – 2 Ara 2026», «28 Ara 2026 – 2 Oca 2027». Tek günse tek tarih; bir uç yoksa öteki. */
export function humanRange(from: string | null | undefined, to: string | null | undefined): string {
  const start = parseIso(from?.slice(0, 10) ?? null);
  const end = parseIso(to?.slice(0, 10) ?? null);
  if (!start) return humanDate(to);
  if (!end) return humanDate(from);
  if (start.year !== end.year) return `${humanDate(from)} – ${humanDate(to)}`;
  const year = String(end.year);
  if (start.month !== end.month) return `${String(start.day)} ${AY[start.month - 1] ?? ""} – ${String(end.day)} ${AY[end.month - 1] ?? ""} ${year}`;
  if (start.day === end.day) return humanDate(from);
  return `${String(start.day)}–${String(end.day)} ${AY[end.month - 1] ?? ""} ${year}`;
}

/** Bugünden o güne takvim günü; geçmişse eksi. Tarih yoksa `null`. */
export function daysFromToday(value: string | null | undefined, today: string = todayIso()): number | null {
  const date = parseIso(value?.slice(0, 10) ?? null);
  const base = parseIso(today);
  if (!date || !base) return null;
  return Math.round((Date.UTC(date.year, date.month - 1, date.day) - Date.UTC(base.year, base.month - 1, base.day)) / 86_400_000);
}

/**
 * Yakın bir vadenin göreli eki: «bugün», «yarın», «3 gün sonra», «2 gün gecikti». `within`: bundan uzak gelecek için ek yok (`null`), tarih
 * tek başına söyler; geçmiş her zaman yazılır, gecikme uzadıkça önemi artar.
 */
export function dueText(value: string | null | undefined, within = 7): string | null {
  const days = daysFromToday(value);
  if (days === null) return null;
  if (days < 0) return `${String(-days)} gün gecikti`;
  if (days === 0) return "bugün";
  if (days === 1) return "yarın";
  return days <= within ? `${String(days)} gün sonra` : null;
}

/** `iso`'dan `delta` gün ötesi, `YYYY-AA-GG`. */
export function shiftDay(iso: string, delta: number): string {
  const day = parseIso(iso);
  if (!day) return iso;
  return new Date(Date.UTC(day.year, day.month - 1, day.day + delta)).toISOString().slice(0, 10);
}

/**
 * Göreli ek, cümle başı: «Bugün», «Yarın», «3 gün sonra», «Dün», «2 gün önce». `late` geçmişi gecikme diye söyler («2 gün gecikti»).
 * `within`: bundan uzak gün için `null`, sütun sessiz kalır.
 */
export function relativeDayText(days: number | null, options: { late?: boolean; within?: number } = {}): string | null {
  if (days === null) return null;
  const { late = false, within = 30 } = options;
  if (Math.abs(days) > within) return null;
  if (days === 0) return "Bugün";
  if (days === 1) return "Yarın";
  if (days > 0) return `${String(days)} gün sonra`;
  if (late) return `${String(-days)} gün gecikti`;
  return days === -1 ? "Dün" : `${String(-days)} gün önce`;
}
